/**
 * 首页搜索逻辑 composable
 * 将搜索状态、搜索历史管理、搜索建议请求等逻辑从 index.vue 中抽离
 */
import type { SearchHistoryItem, TraderListItem } from '~/types/home'
import { getSearchDefault, getSearchSuggest, type SearchDefaultResponse, type SearchSuggestItem } from '~/server/search'
import { resolveExternalUrl } from '~/utils/externalUrl'

const SEARCH_HISTORY_KEY = 'wikibit_search_history'
const SEARCH_SUGGEST_THROTTLE = 300
const EMPTY_SEARCH_DEFAULT_DATA = {
  discoverList: [],
  globalRank: [],
}

type UseHomeSearchOptions = {
  /** 是否在创建搜索上下文时立即加载默认推荐。 */
  immediateDefault?: boolean
  /** 搜索结果需要跳往的外部站点 origin；不传时保留当前应用内跳转。 */
  externalOrigin?: string
  /** 覆盖默认搜索提交行为，用于搜索页内局部刷新等特殊场景。 */
  onSearchSubmit?: (keyword: string) => Promise<void> | void
}

// 将旧版或异常的本地历史归一化为可直接跳转的结构化条目。
const normalizeSearchHistoryItem = (value: unknown): SearchHistoryItem | null => {
  if (value === null || typeof value !== 'object') {
    return null
  }

  const label = Reflect.get(value, 'label')
  const logo = Reflect.get(value, 'logo')
  const target = Reflect.get(value, 'target')
  const alias = Reflect.get(value, 'alias')

  if (typeof label !== 'string' || label.trim().length === 0) {
    return null
  }

  const normalizedItem = {
    label: label.trim(),
    ...(typeof logo === 'string' ? { logo } : {}),
  }

  if (target === 'exchange' && typeof alias === 'string' && alias.trim().length > 0) {
    return {
      ...normalizedItem,
      target: 'exchange',
      alias: alias.trim(),
    }
  }

  return {
    ...normalizedItem,
    target: 'search',
  }
}

export const useHomeSearch = (options: UseHomeSearchOptions = {}) => {
  const searchQuery = ref('')
  const isFocused = ref(false)
  const searchHistory = useState<SearchHistoryItem[]>('wikibit-shared-search-history', () => [])
  const searchSuggestList = ref<TraderListItem[]>([])
  const hasSearched = ref(false)
  // 标记搜索建议请求是否未完成，用于避免输入时推荐列表瞬间清空。
  const isSearchSuggestPending = ref(false)
  let searchSuggestThrottleTimer: ReturnType<typeof setTimeout> | null = null
  let lastSearchSuggestInvokeAt = 0
  let latestSearchSuggestKeyword = ''

  const { localePath } = useAppI18n()

  /** 根据配置生成站内路径或完整外部 URL。 */
  const resolveSearchTarget = (localizedPath: string) =>
    options.externalOrigin ? resolveExternalUrl(options.externalOrigin, localizedPath) : localizedPath

  /** 搜索结果默认在新窗口打开；存在外部 origin 时显式允许 Nuxt 外跳。 */
  const openSearchTarget = async (localizedPath: string) => {
    await navigateTo(resolveSearchTarget(localizedPath), {
      external: Boolean(options.externalOrigin),
      open: {
        target: '_blank',
      },
    })
  }

  // ─── 搜索默认数据（SSR 兼容） ───
  const searchDefaultAsyncData = useAsyncData(
    'home-search-default',
    async (): Promise<SearchDefaultResponse> => {
      try {
        const res = await getSearchDefault()

        if (!res?.succeed) {
          return EMPTY_SEARCH_DEFAULT_DATA
        }
        return res.data || EMPTY_SEARCH_DEFAULT_DATA
      } catch {
        return EMPTY_SEARCH_DEFAULT_DATA
      }
    },
    {
      immediate: options.immediateDefault ?? true,
    },
  )
  const searchDefaultData = searchDefaultAsyncData.data

  // ─── computed ───
  const trimmedSearchQuery = computed(() => searchQuery.value.trim())

  /** 是否有初始内容（热门交易商或搜索历史），用于决定 focus 时是否立即弹窗 */
  const hasInitialContent = computed(() => {
    const globalRank = searchDefaultData.value?.globalRank ?? []
    // const globalRank = []
    return globalRank.length > 0 || searchHistory.value.length > 0
  })

  const popularTraders = computed<TraderListItem[]>(() => {
    if (trimmedSearchQuery.value) {
      if (searchSuggestList.value.length > 0) {
        return searchSuggestList.value
      }

      if (isSearchSuggestPending.value) {
        return searchDefaultData.value?.globalRank || []
        // return []
      }
      return searchSuggestList.value
    }
    return searchDefaultData.value?.globalRank || []
    // return []
  })

  const traderTags = computed(() => {
    return (searchDefaultData.value?.discoverList || []).map((item) => ({
      label: item.name,
      icon: item.icon,
      code: item.code,
      alias: item.alias,
    }))
  })

  // ─── 搜索历史持久化 ───
  const saveSearchHistory = (list: readonly SearchHistoryItem[]) => {
    searchHistory.value = [...list]

    if (!import.meta.client) {
      return
    }

    window.localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(list))
  }

  // 将最近一次搜索行为移到历史首位，同名条目以最新跳转目标为准。
  const addSearchHistory = (item: Readonly<SearchHistoryItem>) => {
    const normalizedItem = normalizeSearchHistoryItem(item)
    if (!normalizedItem) {
      return
    }

    const nextHistory = [
      normalizedItem,
      ...searchHistory.value.filter(
        (historyItem) => historyItem.label.toLowerCase() !== normalizedItem.label.toLowerCase(),
      ),
    ]

    saveSearchHistory(nextHistory)
  }

  const deleteHistoryItem = (index: number) => {
    const nextHistory = [...searchHistory.value]
    nextHistory.splice(index, 1)
    saveSearchHistory(nextHistory)
  }

  const clearAllHistory = () => {
    saveSearchHistory([])
  }

  // 使用当前语言打开关键词搜索页。
  const openSearchPage = async (keyword: string) => {
    const normalizedKeyword = keyword.trim()
    if (normalizedKeyword.length === 0) {
      return
    }

    await openSearchTarget(localePath(`/search?keyword=${encodeURIComponent(normalizedKeyword)}`))
  }

  // 使用当前语言打开交易商详情页。
  const openTraderDetail = async (alias: string) => {
    const normalizedAlias = alias.trim()
    if (normalizedAlias.length === 0) {
      return
    }

    await openSearchTarget(localePath(`/exchanges/${encodeURIComponent(normalizedAlias)}`))
  }

  // 点击历史时保留最近使用顺序，并根据历史目标恢复原跳转或页内搜索行为。
  const selectHistoryItem = async (item: Readonly<SearchHistoryItem>) => {
    searchQuery.value = item.label
    addSearchHistory(item)

    if (item.target === 'exchange') {
      await openTraderDetail(item.alias || item.label)
      return
    }

    await (options.onSearchSubmit ? options.onSearchSubmit(item.label) : openSearchPage(item.label))
  }

  // 点击搜索下拉交易商时写入详情历史，并打开对应详情页。
  const selectTraderItem = async (item: Readonly<TraderListItem>) => {
    const alias = item.alias?.trim() ?? ''
    const normalizedName = item.name?.trim() ?? ''
    const label = normalizedName.length > 0 ? normalizedName : alias

    if (alias.length === 0 || label.length === 0) {
      return
    }

    addSearchHistory({
      label,
      ...(typeof item.icon === 'string' ? { logo: item.icon } : {}),
      target: 'exchange',
      alias,
    })
    await openTraderDetail(alias)
  }

  const fetchSearchSuggest = async (keyword = searchQuery.value.trim()) => {
    if (!keyword) {
      isSearchSuggestPending.value = false
      hasSearched.value = false
      searchSuggestList.value = []
      return
    }
    isSearchSuggestPending.value = true
    try {
      const res = await getSearchSuggest(keyword)
      hasSearched.value = true
      searchSuggestList.value = (Array.isArray(res.data) ? res.data : []).map((item: SearchSuggestItem) => ({
        ...item,
        icon: item.icon,
        name: item.showName || item.brandCode || item.code,
        score: item.textScore || item.score,
        status: item.annotation,
        code: item.code,
        color: item.color,
        alias: item.alias,
        years: item?.sortedLabel?.find((item1) => item1?.labelType === 101)?.content || '--',
      }))
    } catch {
      hasSearched.value = true
      searchSuggestList.value = []
    } finally {
      isSearchSuggestPending.value = false
    }
  }

  const loadSearchDefault = async () => {
    if (searchDefaultData.value) {
      return
    }

    await searchDefaultAsyncData.execute()
  }

  const clearSearchSuggestThrottle = () => {
    if (!searchSuggestThrottleTimer) {
      return
    }

    clearTimeout(searchSuggestThrottleTimer)
    searchSuggestThrottleTimer = null
  }

  const scheduleFetchSearchSuggest = (keyword = searchQuery.value) => {
    const normalizedKeyword = keyword.trim()
    latestSearchSuggestKeyword = normalizedKeyword

    if (!normalizedKeyword) {
      clearSearchSuggestThrottle()
      lastSearchSuggestInvokeAt = 0
      isSearchSuggestPending.value = false
      void fetchSearchSuggest('')
      return
    }

    isSearchSuggestPending.value = true
    const now = Date.now()
    const remaining = SEARCH_SUGGEST_THROTTLE - (now - lastSearchSuggestInvokeAt)

    if (remaining <= 0) {
      clearSearchSuggestThrottle()
      lastSearchSuggestInvokeAt = now
      void fetchSearchSuggest(normalizedKeyword)
      return
    }

    clearSearchSuggestThrottle()
    searchSuggestThrottleTimer = setTimeout(() => {
      searchSuggestThrottleTimer = null
      lastSearchSuggestInvokeAt = Date.now()
      void fetchSearchSuggest(latestSearchSuggestKeyword)
    }, remaining)
  }

  const handleFocus = () => {
    isFocused.value = true

    if (!searchQuery.value.trim() && !searchDefaultData.value) {
      void loadSearchDefault()
    }
    if (searchQuery.value.trim() && searchSuggestList.value.length === 0) {
      scheduleFetchSearchSuggest(searchQuery.value)
    }
  }

  const handleSearchInput = () => {
    // 没有初始内容时，输入关键词后才弹窗，清空时收回
    if (!hasInitialContent.value) {
      isFocused.value = searchQuery.value.trim().length > 0
    }
    scheduleFetchSearchSuggest(searchQuery.value)
  }

  // ─── 搜索操作 ───
  const handleSearch = async () => {
    const keyword = searchQuery.value.trim()
    if (keyword.length === 0) {
      return
    }

    clearSearchSuggestThrottle()
    addSearchHistory({
      label: keyword,
      target: 'search',
    })

    hasSearched.value = false
    isSearchSuggestPending.value = false
    searchSuggestList.value = []
    await (options.onSearchSubmit ? options.onSearchSubmit(keyword) : openSearchPage(keyword))
    isFocused.value = false
    // 让输入框真正失焦，否则再次点击不会触发 focus 事件
    if (import.meta.client) {
      ;(document.activeElement as HTMLElement | null)?.blur()
    }

    // await fetchSearchSuggest(keyword)
  }

  const onBlur = () => {
    setTimeout(() => {
      isFocused.value = false
    }, 200)
  }

  // 清空历史/无初始内容时自动关闭弹窗
  watch(hasInitialContent, (val) => {
    if (!val && !searchQuery.value.trim()) {
      isFocused.value = false
    }
  })
  onUnmounted(() => {
    clearSearchSuggestThrottle()
  })

  // ─── 初始化搜索历史（仅客户端） ───
  const initSearchHistory = () => {
    if (!import.meta.client) return

    const savedHistory = window.localStorage.getItem(SEARCH_HISTORY_KEY)

    if (savedHistory) {
      try {
        const parsedHistory: unknown = JSON.parse(savedHistory)
        searchHistory.value = Array.isArray(parsedHistory)
          ? parsedHistory.map(normalizeSearchHistoryItem).filter((item): item is SearchHistoryItem => item !== null)
          : []
      } catch {
        searchHistory.value = []
      }
    }
  }

  return {
    searchQuery,
    isFocused,
    searchHistory,
    searchSuggestList,
    hasSearched,
    isSearchSuggestPending,
    searchDefaultData,
    popularTraders,
    traderTags,
    saveSearchHistory,
    addSearchHistory,
    deleteHistoryItem,
    clearAllHistory,
    selectHistoryItem,
    selectTraderItem,
    fetchSearchSuggest,
    loadSearchDefault,
    handleFocus,
    handleSearchInput,
    handleSearch,
    onBlur,
    initSearchHistory,
    searchDefaultAsyncData,
  }
}
